import { MaterialReactTable } from 'material-react-table'
import React, { useRef, useMemo } from 'react';
import { download, generateCsv, mkConfig } from 'export-to-csv';
import { Button } from '@mui/material';
import AlertTemplateMessage from '../../Configuration/AlertTemplate/AlertTemplateMessage';
import { createExportRequest } from '../../../services/dashboardService';
import { ErrorToast } from '../../../CustomHooks/ErrorToast';
import { SuccessToast } from '../../../CustomHooks/SuccessToast';


function checkTableDataExceeded(tableData) {
  // Record exceeded above 5000 rows
  // case 1: empty array
  if (Array.isArray(tableData) && tableData.length === 0) {
    return false;
  }

  // case 2: [ { Result: 0 } ]
  if (
    Array.isArray(tableData) &&
    tableData.length === 1 &&
    tableData[0]?.Result === 0
  ) {
    return true;
  }

  // case 3: object with Data array (your success response)
  if (
    typeof tableData === "object" &&
    tableData !== null &&
    Array.isArray(tableData.Data)
  ) {
    return false;
  }

  // default
  return false;
}

// Utility to convert various date string formats to local timezone string
const formatUTCToOffset = (dateStr, offsetMinutes = 0) => {
  if (!dateStr) return "";

  let date;

  // dd-mm-yyyy hh:mm:ss (UTC)
  if (/^\d{2}-\d{2}-\d{4} \d{2}:\d{2}:\d{2}$/.test(dateStr)) {
    const [day, month, yearAndTime] = dateStr.split("-");
    const [year, time] = yearAndTime.split(" ");
    const [hour, minute, second] = time.split(":");

    date = new Date(Date.UTC(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second)
    ));
  }

  // ISO format
  else if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(dateStr)) {
    date = new Date(dateStr.endsWith("Z") ? dateStr : dateStr + "Z");
  }

  // yyyy-mm-dd hh:mm:ss (UTC)
  else if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(dateStr)) {
    const [datePart, timePart] = dateStr.split(" ");
    const [year, month, day] = datePart.split("-");
    const [hour, minute, second] = timePart.split(":");

    date = new Date(Date.UTC(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second)
    ));
  }

  // fallback
  else {
    date = new Date(dateStr);
  }

  if (isNaN(date.getTime())) return dateStr;

  const offsetDate = new Date(date.getTime() + offsetMinutes * 60 * 1000);

  const pad = n => n.toString().padStart(2, "0");

  return `${pad(offsetDate.getUTCDate())}-${pad(offsetDate.getUTCMonth() + 1)}-${offsetDate.getUTCFullYear()} ` +
         `${pad(offsetDate.getUTCHours())}:${pad(offsetDate.getUTCMinutes())}:${pad(offsetDate.getUTCSeconds())}`;
};

// Utility to convert various date string formats to local timezone string
const formatUTCToLocal = (dateStr) => {
  if (!dateStr) return "";

  let date;

  // dd-mm-yyyy hh:mm:ss (UTC)
  if (/^\d{2}-\d{2}-\d{4} \d{2}:\d{2}:\d{2}$/.test(dateStr)) {
    const [day, month, yearAndTime] = dateStr.split("-");
    const [year, time] = yearAndTime.split(" ");
    const [hour, minute, second] = time.split(":");

    date = new Date(Date.UTC(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second)
    ));
  }

  // ISO format
  else if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(dateStr)) {
    date = new Date(dateStr.endsWith("Z") ? dateStr : dateStr + "Z");
  }

  // yyyy-mm-dd hh:mm:ss (UTC)
  else if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(dateStr)) {
    const [datePart, timePart] = dateStr.split(" ");
    const [year, month, day] = datePart.split("-");
    const [hour, minute, second] = timePart.split(":");

    date = new Date(Date.UTC(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second)
    ));
  }

  // fallback
  else {
    date = new Date(dateStr);
  }

  if (isNaN(date.getTime())) return dateStr;

  // Use local getters so output is local time
  const localDate = new Date(date.getTime());

  const pad = n => n.toString().padStart(2, "0");

  return `${pad(localDate.getDate())}-${pad(localDate.getMonth() + 1)}-${localDate.getFullYear()} ` +
         `${pad(localDate.getHours())}:${pad(localDate.getMinutes())}:${pad(localDate.getSeconds())}`;
};


// Utility to process data keys: remove spaces from all keys in each row
const processTableDataKeys = (tableData) => {
  return tableData.map(row => {
    const newRow = {};
    Object.keys(row).forEach(key => {
      newRow[key.replace(/\s+/g, "")] = row[key];
    });
    return newRow;
  });
};

// Utility to map original keys (with spaces) to keys without spaces
const getKeyMap = (tableData) => {
  if (!tableData.length) return {};
  const keyMap = {};
  Object.keys(tableData[0]).forEach(key => {
    // Exclude columns that end with _UTC
    if (!key.endsWith("_UTC")) {
      keyMap[key.replace(/\s+/g, "")] = key;
    }
  });
  return keyMap;
};

const getTableViewColumns = (tableData, keyMap) => {
  const columnKeys = Object.keys(keyMap);
  // Get all original column keys (including _UTC ones) for DateTime detection
  const allOriginalKeys = tableData.length > 0 ? Object.keys(tableData[0]) : [];


  return columnKeys.map((key) => {
    const accessorKey = key; // keys already have no spaces after processing
    const header = keyMap[key]; // header with spaces
    
    // Check if this is a DateTime column by looking for corresponding _UTC column in original data
    // Since we're working with processed data, we need to check for the processed key version
    const processedUTCKey = `${header}_UTC`.replace(/\s+/g, "");
    const isDateTimeColumn = tableData.length > 0 && allOriginalKeys.includes(processedUTCKey);
    
    let filterSelectOptions;
    if ([].includes(key)) {
      filterSelectOptions = [
        ...new Set(
          tableData
            .map((row) => row[key])
            .filter((val) => val !== undefined && val !== null)
            .map((val) => String(val))
        ),
      ];
    }

    return {
      accessorKey,
      header,
      filterVariant: filterSelectOptions ? "multi-select" : "text",
      filterSelectOptions,
      Cell: ({ cell }) => {
        const cellValue = cell.getValue();

        // Format date strings using OffsetMinutes column if present; otherwise convert to local time
        if (isDateTimeColumn) {
          const row = cell.row?.original || {};
          const hasOffset = Object.prototype.hasOwnProperty.call(row, 'OffsetMinutes') || Object.prototype.hasOwnProperty.call(row, 'offsetMinutes');
          if (hasOffset) {
            const offsetVal = row.OffsetMinutes ?? row.offsetMinutes ?? 0;
            return formatUTCToOffset(cellValue, offsetVal);
          }
          return formatUTCToLocal(cellValue);
        }
        // For other data types, return as is
        return cellValue;
      },
      filterFn: filterSelectOptions
        ? "arrIncludesSome"
        : (row, columnId, filterValue) => {
            const cellValue = row.getValue(columnId);
            return (cellValue ?? "")
              .toString()
              .toLowerCase()
              .includes(filterValue.toLowerCase());
          },
    };
  });
};

const TableDetails = ({
    tableData = [], 
    csvFileName = 'table-data',
    exportRequestParams = null
}) => {
    // Process data to remove spaces from keys
    const isTableDataExceeded = checkTableDataExceeded(tableData);
    if(isTableDataExceeded){
      tableData = [];
    }
    const processedTableData = useMemo(() => processTableDataKeys(tableData), [tableData]);
    const keyMap = useMemo(() => getKeyMap(tableData), [tableData]);
    const columns = useMemo(() => getTableViewColumns(processedTableData, keyMap), [processedTableData, keyMap]);

    const tableRef = useRef();

    const handleExportData = async (table) => {
        if (isTableDataExceeded) {
            // Call createExportRequest for large datasets
            // if (!exportRequestParams) {
            //     ErrorToast
            //     alert("Export request parameters not provided. Please contact administrator.");
            //     return;
            // }
            
            try {
                const response = await createExportRequest(exportRequestParams);
                if (response?.Status === "success") {
                    SuccessToast("Export request submitted successfully. You will receive the data via email.");
                } else {
                    ErrorToast(response?.Message || "Failed to submit export request.");
                }
            } catch (error) {
                console.error("Error submitting export request:", error);
                ErrorToast("Failed to submit export request. Please try again later.");
            }
            return;
        }

        // Normal CSV export flow for smaller datasets
        const filteredRows = table.getFilteredRowModel().rows;
        const filteredData = filteredRows.map((row) => row.original);

        if (!filteredData.length) {
            ErrorToast("No data to export!");
            return;
        }
        
        const visibleColumnIds = table.getVisibleLeafColumns().map((col) => col.id);
        // Get all processed column keys (including _UTC ones) for DateTime detection
        const allProcessedKeys = processedTableData.length > 0 ? Object.keys(processedTableData[0]) : [];
        
        const exportColumns = columns
            .filter((col) => visibleColumnIds.includes(col.accessorKey))
            .map((col) => ({
                accessorKey: col.accessorKey,
                header: col.header,
                isDateTime: allProcessedKeys.includes(`${col.header}_UTC`.replace(/\s+/g, ""))
            }));

        const exportData = filteredData.map((row) => {
            const newRow = {};
              exportColumns.forEach((col) => {
                let value = row[col.accessorKey];
                // Apply UTC to OffsetMinutes conversion for DateTime columns; fall back to local conversion
                if (col.isDateTime) {
                  const hasOffset = Object.prototype.hasOwnProperty.call(row, 'OffsetMinutes') || Object.prototype.hasOwnProperty.call(row, 'offsetMinutes');
                  if (hasOffset) {
                    const offsetVal = row.OffsetMinutes ?? row.offsetMinutes ?? 0;
                    value = formatUTCToOffset(value, offsetVal);
                  } else {
                    value = formatUTCToLocal(value);
                  }
                }
                newRow[col.header] = value === null || value === undefined ? "" : value;
              });
            return newRow;
        });
        
        const csvConfig = mkConfig({
            useKeysAsHeaders: true,
            filename: csvFileName,
        });

        let csv = generateCsv(csvConfig)(exportData);

        // Add the message at the end of the CSV
        csv += '\n*This is a system generated report';
        
        download(csvConfig)(csv);
    };

    return (
        <div className="col-lg-12 TabulerReportMainDiv MatReactTableDiv" style={{ height: 'calc(100vh - 210px)' }}>
            <div className="card-body p-0">
                <MaterialReactTable
                    columns={columns}
                    data={processedTableData}
                    tableInstanceRef={tableRef}
                    enableGlobalFilter={true}
                    enableStickyHeader
                    enableColumnResizing={true}
                    globalFilterFn= "contains"
                    columnResizeMode="onEnd"
                    enableColumnOrdering={true}
                    initialState={{ showColumnFilters: false }}
                    muiTableContainerProps={{
                        className: 'MaterialTableJs',
                        sx: {
                            maxHeight: '100%',
                            overflow: 'auto',
                        },
                    }}
                    renderTopToolbarCustomActions={({table}) => (
                        <Button variant="outlined" onClick={() => handleExportData(table)}>
                            Export
                        </Button>
                    )}
                    muiTableHeadProps={{
                      className: "myHeader",
                        sx: {
                            display: processedTableData.length === 0 ? 'none' : 'table-header-group',
                        },
                    }}
                    renderEmptyRowsFallback={({ table }) => (
                      isTableDataExceeded ? (
                        <div className='col-lg-12 HMHeader' style={{ width: "100%", display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                          <AlertTemplateMessage
                            message={
                              "Data contains more than 5000 records. Please use the Export button to download the complete data."
                            }
                          />
                        </div>
                      ) : (
                        <div className='col-lg-12 HMHeader' style={{ width: "100%", display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                          <span>No Records to display</span>
                        </div>
                      )
                    )}
                />
            </div>
        </div>
    );
};

export default TableDetails;